import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// MC-553: drag-and-drop reordering for stage groups and for stages
// within a group — the id-keyed sibling of `workflows--reorder` (which
// keys on slug). Each row carries `data-id`, so Sortable's `toArray()`
// returns the new id order directly. On drop we POST that order to the
// `url` value under the `param` key; an optional `scopeParam`/`scopeId`
// pair rides along (the stage list sends `stage_group_id` so the server
// knows which group the dropped stages belong to). A failed POST rolls
// the move back locally so the visible order never drifts from the
// server's, exactly like the board reorder.
//
// MC-1009: keyboard reordering rides alongside the mouse drag, additive
// and opt-in. A handle the caller made focusable (`tabindex="0"`) moves
// its row up/down under ArrowUp/ArrowDown and persists the same way a
// drag does. Callers that leave handles unfocusable (stage groups,
// stages) never reach this path, so their behaviour is unchanged.
export default class extends Controller {
  static values = {
    url: String,
    param: String,
    scopeParam: String,
    scopeId: String
  }
  static targets = ["handle"]

  connect() {
    this.sortable = Sortable.create(this.element, {
      animation: 150,
      handle: "[data-reorder-target='handle']",
      dataIdAttr: "data-id",
      onStart: () => { this.previousOrder = this.sortable.toArray() },
      onEnd: this.persist.bind(this)
    })
    this.previousOrder = this.sortable.toArray()
    this.onKeydown = this.onKeydown.bind(this)
    this.element.addEventListener("keydown", this.onKeydown)
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.onKeydown)
    if (this.sortable) {
      this.sortable.destroy()
      this.sortable = null
    }
  }

  // MC-1009: ArrowUp/ArrowDown on a focused handle moves its row one slot
  // and persists — the keyboard equivalent of a drag. Confined to the
  // row's siblings inside this list, so a rule can no more cross into
  // another topic's group by keyboard than by mouse. Handles that aren't
  // focusable never receive the keydown, so non-inbound reorder surfaces
  // are untouched.
  onKeydown(event) {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return

    const handle = event.target.closest("[data-reorder-target='handle']")
    if (!handle || !this.element.contains(handle)) return

    const item = handle.closest("[data-id]")
    if (!item) return

    const moved =
      event.key === "ArrowUp" ? this.moveUp(item) : this.moveDown(item)
    if (!moved) return

    event.preventDefault()
    handle.focus()
    this.persist()
  }

  moveUp(item) {
    const prev = item.previousElementSibling
    if (!prev) return false
    this.element.insertBefore(item, prev)
    return true
  }

  moveDown(item) {
    const next = item.nextElementSibling
    if (!next) return false
    this.element.insertBefore(next, item)
    return true
  }

  async persist() {
    const ids = this.sortable.toArray()
    const payload = { [this.paramValue]: ids }
    if (this.hasScopeParamValue && this.scopeParamValue) {
      payload[this.scopeParamValue] = this.scopeIdValue
    }

    const response = await fetch(this.urlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      // Server rejected the new order — revert the DOM so what the
      // operator sees matches what the server holds.
      this.sortable.sort(this.previousOrder, true)
    } else {
      this.previousOrder = this.sortable.toArray()
    }
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.getAttribute("content") : ""
  }
}
